import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { get } from '../api-client';

/**
 * GET /api/Questionnaires/Search
 *
 * Mirrors Flutter `getFormsList()` →
 * `scheduling_data_source_impl.dart:628-653`. Returns the practice's
 * questionnaires for the Send Form picker.
 *
 * Flutter filters to `status === 'Active'` after fetch
 * (forms_selection_widget.dart:66) — we mirror that here so callers
 * always see picker-eligible rows.
 */

export interface QuestionnaireDatum {
  id?: string;
  title?: string;
  /** "Active" | "Inactive" | "Draft" — picker only shows "Active". */
  status?: string;
  practitionerId?: string;
  totalQuestions?: number;
  organizationId?: string;
  isArchive?: boolean;
  /** Backend ships this as `photoURL` (capital URL) — see model. */
  photoURL?: string;
}

export interface QuestionnaireList {
  currentPage?: number;
  totalPage?: number;
  count?: number;
  questionnaireData?: QuestionnaireDatum[];
}

interface UseFormsListParams {
  organizationId: string;
  /** Provider id — required by backend to scope per-practitioner forms. */
  providerId?: string | null;
  /** Filter to `appointmentType.code` ("ROUTINE" | "FOLLOWUP" | "WALKIN" …). */
  appointmentType?: string | null;
  /** Free-text search; mapped to ?Search= query param. */
  search?: string;
  pageNumber?: number;
  pageSize?: number;
  isArchived?: boolean;
}

export function useFormsList(params: UseFormsListParams | null) {
  return useQuery({
    queryKey: ['forms-list', params],
    enabled: !!params?.organizationId && !!params?.providerId,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      if (!params) throw new Error('useFormsList called without params');
      const qs = new URLSearchParams();
      qs.set('OrganizationId', params.organizationId);
      qs.set('IsArchived', String(params.isArchived ?? false));
      qs.set('PageNumber', String(params.pageNumber ?? 1));
      qs.set('PageSize', String(params.pageSize ?? 20));
      if (params.search) qs.set('Search', params.search);
      if (params.providerId) qs.set('PractitionerId', params.providerId);
      if (params.appointmentType) qs.set('AppointmentType', params.appointmentType);
      const data = await get<QuestionnaireList>(
        `/api/Questionnaires/Search?${qs.toString()}`,
        { signal },
      );
      // Flutter filters out non-Active rows after fetch — same here so the
      // picker only shows usable forms.
      return {
        ...data,
        questionnaireData: (data.questionnaireData ?? []).filter(
          (f) => f.status === 'Active',
        ),
      };
    },
  });
}
